import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Linkedin, Home, ArrowLeft } from "lucide-react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";

/* ── Team data (sourced from kyoown.com/team) ───────────────────────────── */
const BOARD = [
  {
    name: "Shaquille Douglas",
    title: "Non-Executive Chairman",
    subtitle: null,
    photo: "https://www.cyrusfieldtech.com/kyo/assets/images/shaq.jpg",
    linkedin: "https://www.linkedin.com/in/shaquille-douglas-a4a568a2/",
  },
  {
    name: "Aldo Antonio Muir",
    title: "President & CEO",
    subtitle: null,
    photo: "https://www.cyrusfieldtech.com/kyo/assets/images/Aldo.png",
    linkedin: "https://www.linkedin.com/in/aldo-antonio-muir-10b2965a",
  },
  {
    name: "Dwight Burchell",
    title: "Company Secretary",
    subtitle: "Chief Financial Officer",
    photo: "https://www.cyrusfieldtech.com/kyo/assets/images/dwight.png",
    linkedin: "https://www.linkedin.com/in/dwight-n-burchell-bba-msc-fca-a1bba49b/",
  },
  {
    name: "Alex Moore",
    title: "Warehouse and Logistics",
    subtitle: "USA Operations",
    photo: "https://www.cyrusfieldtech.com/kyo/assets/images/alex-img.png",
    linkedin: "#",
  },
  {
    name: "Marcel Anderson",
    title: "Executive Director",
    subtitle: null,
    photo: null,
    linkedin: "https://www.linkedin.com/in/marcel-anderson/",
  },
  {
    name: "Aracely Hampton",
    title: "Country Manager — Dominican Republic",
    subtitle: "Executive Director",
    photo: "https://www.cyrusfieldtech.com/kyo/assets/images/Event-Speakers-9-2.png",
    linkedin: "#",
  },
  {
    name: "Marc-Anthony Marsh-Howard",
    title: "Non-Executive Director",
    subtitle: null,
    photo: "https://www.cyrusfieldtech.com/kyo/assets/images/Marc.jpeg",
    linkedin: "https://www.linkedin.com/in/mmarshhoward/",
  },
  {
    name: "Mary Lawson",
    title: "Country Manager — Grenada & St. Vincent",
    subtitle: "Executive Director",
    photo: "https://www.cyrusfieldtech.com/kyo/assets/images/Event-Speakers-11-2.png",
    linkedin: "#",
  },
  {
    name: "Robert Meek",
    title: "Country Manager — Trinidad and Tobago",
    subtitle: "Executive Director",
    photo: "https://www.cyrusfieldtech.com/kyo/assets/images/Event-Speakers-12-2.png",
    linkedin: "#",
  },
];

/* ── Avatar with initials fallback ─────────────────────────────────────── */
function Avatar({ photo, name }) {
  const [err, setErr] = useState(false);
  const initials = name
    .split(" ")
    .slice(0, 2)
    .map((w) => w[0])
    .join("")
    .toUpperCase();

  if (!photo || err) {
    return (
      <div className="w-[200px] h-[200px] rounded-full bg-gradient-to-br from-[#ED2E24] to-[#8b1a16] flex items-center justify-center text-white text-4xl font-bold select-none shadow-[0_12px_40px_rgba(237,46,36,0.25)]">
        {initials}
      </div>
    );
  }

  return (
    <img
      src={photo}
      alt={name}
      onError={() => setErr(true)}
      className="w-[200px] h-[200px] rounded-full object-cover shadow-[0_12px_40px_rgba(0,0,0,0.18)] border-4 border-white"
    />
  );
}

/* ── Single member card ──────────────────────────────────────────────────── */
function MemberCard({ member }) {
  return (
    <div
      className="flex flex-col items-center text-center group"
      data-testid={`member-card-${member.name.replace(/\s+/g, "-").toLowerCase()}`}
    >
      <div className="mb-5 transition-transform duration-300 group-hover:scale-[1.03]">
        <Avatar photo={member.photo} name={member.name} />
      </div>

      <h3 className="text-[17px] font-bold text-[#1a1a1a] leading-snug mb-1">
        {member.name}
      </h3>
      <p className="text-[13px] text-gray-500 leading-snug">{member.title}</p>
      {member.subtitle && (
        <p className="text-[12px] text-[#ED2E24] font-medium mt-0.5">{member.subtitle}</p>
      )}

      <a
        href={member.linkedin}
        onClick={(e) => member.linkedin === "#" && e.preventDefault()}
        target={member.linkedin !== "#" ? "_blank" : undefined}
        rel="noreferrer"
        aria-label={`${member.name} LinkedIn`}
        className="mt-3 w-8 h-8 rounded-full bg-[#e9e9e9] hover:bg-[#0077b5] flex items-center justify-center transition-colors group/li"
      >
        <Linkedin size={14} className="text-[#666] group-hover/li:text-white transition-colors" />
      </a>
    </div>
  );
}

/* ══════════════════════════════════════════════════════
   Investors / Corporate Governance Page
══════════════════════════════════════════════════════ */
export default function InvestorsPage() {
  const navigate = useNavigate();

  useEffect(() => {
    if (window.location.hash) {
      const id = window.location.hash.slice(1);
      const timer = setTimeout(() => {
        const el = document.getElementById(id);
        if (el) el.scrollIntoView({ behavior: "smooth" });
      }, 100);
      return () => clearTimeout(timer);
    }
  }, []);

  return (
    <div className="min-h-screen bg-white" data-testid="investors-page">
      {/* Shared Navbar */}
      <Navbar />

      {/* ── Hero Banner ─────────────────────────────────────────────────── */}
      <div className="relative w-full h-[420px] overflow-hidden" data-testid="investors-hero">
        {/* Background photo */}
        <img
          src="https://images.unsplash.com/photo-1758691736975-9f7f643d178e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzl8MHwxfHNlYXJjaHwxfHxkaXZlcnNlJTIwYnVzaW5lc3MlMjB0ZWFtJTIwcHJvZmVzc2lvbmFsJTIwZ3JvdXAlMjBwb3J0cmFpdCUyMHNtaWxpbmd8ZW58MHx8fHwxNzg4NDg3NzYyfDA&ixlib=rb-4.1.0&q=85"
          alt="KYo Group team"
          className="absolute inset-0 w-full h-full object-cover object-center scale-105"
        />
        {/* Red/dark gradient overlay */}
        <div className="absolute inset-0 bg-gradient-to-br from-[#ED2E24]/80 via-[#c9231a]/70 to-[#212327]/80" />

        {/* Back link */}
        <button
          onClick={() => navigate("/")}
          className="absolute top-5 left-5 flex items-center gap-2 text-white/70 hover:text-white text-sm transition-colors z-10"
          data-testid="investors-back-btn"
        >
          <ArrowLeft size={16} />
          Back to KYo Group
        </button>

        {/* Heading */}
        <div className="relative z-10 h-full flex items-center justify-center px-6">
          <div className="text-center">
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-tight tracking-tight drop-shadow-lg">
              KYo Group Corporate Governance
            </h1>
          </div>
        </div>
      </div>

      {/* ── Board of Directors ───────────────────────────────────────────── */}
      <section id="main-content" className="py-20 px-5 lg:px-6 max-w-[1240px] mx-auto" data-testid="board-section">

        {/* Section intro */}
        <div className="text-center mb-16">
          <h2 className="text-[34px] lg:text-[40px] font-bold text-[#1a1a1a]">Board of Directors</h2>
          <div className="mx-auto mt-3 w-14 h-1 bg-[#ED2E24] rounded-full mb-8" />
          <p className="text-[15px] text-gray-500 max-w-[680px] mx-auto leading-relaxed">
            Corporate governance continues to be a core priority for KYo Group and is integral to
            achieving the Company's strategic goals.
          </p>
          <p className="text-[15px] text-gray-500 max-w-[680px] mx-auto leading-relaxed mt-2">
            Our policy framework and guiding principles are designed to uphold the highest levels of
            transparency, accountability and independence, ensuring the best interests of all
            stakeholders are consistently served.
          </p>
        </div>

        {/* Team grid */}
        <div
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-x-8 gap-y-14"
          data-testid="board-grid"
        >
          {BOARD.map((m) => (
            <MemberCard key={m.name} member={m} />
          ))}
        </div>
      </section>

      {/* ── Footer ──────────────────────────────────────────────────────── */}
      <Footer />
    </div>
  );
}
